/** Per-league cutoff lookup and rank tiers sized by each league's team count. */

import { getParentLeague, type LeagueKey, leagueKeyOrNull } from './league';
import { getTeamsForLeague } from './teams';

/**
 * A league's cutoff entry, read through its parent league (NBA Preseason reads the NBA's).
 * Undefined with no entry or no league, so callers skip the label or score.
 */
export function cutoffsForLeague<T>(
  table: Partial<Record<LeagueKey, T>>,
  league: string | null | undefined,
): T | undefined {
  const key = leagueKeyOrNull(league);
  return key ? table[getParentLeague(key)] : undefined;
}

/** Number of teams in the league's registry (its parent's for an NBA variant), or null when it has none. */
export function leagueTeamCount(league: string | null | undefined): number | null {
  const key = leagueKeyOrNull(league);
  const teams = key ? getTeamsForLeague(getParentLeague(key)) : null;
  return teams ? Object.keys(teams).length : null;
}

export type RankTier = 'top' | 'middle' | 'bottom';

/** Thirds of the league: NBA 1-10, 11-20, 21-30; WNBA 1-5, 6-10, 11-15. Rank 1 is the best. */
export function rankTier(rank: number, teamCount: number): RankTier {
  if (rank <= teamCount / 3) return 'top';
  if (rank <= (2 * teamCount) / 3) return 'middle';
  return 'bottom';
}

/** The rank's tier in this league, or null when the league or its team count is unknown. */
export function leagueRankTier(rank: number, league: string | null | undefined): RankTier | null {
  const teamCount = leagueTeamCount(league);
  return teamCount ? rankTier(rank, teamCount) : null;
}
